import React, { useState, useEffect } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  Sparkles, Cat, Coffee, Flame, Headphones, Wand2, Sun,
  Cookie, Pencil, CupSoda, Music2, BrainCircuit, Flower2, Shuffle,
  Footprints, Wand, Gem, Feather, Snail, Armchair, Rocket,
  Palette, Hammer, Waypoints, Cog, GitBranch, FlaskConical, Flower,
  MapPin, Blocks, Orbit, Zap, Wind, Map,
  Ghost, Compass, Telescope, CloudFog, TreePine, Shell, Binoculars,
  Mountain, Unplug, Shrub, MoonStar, Tent, Anchor, Milestone,
  AlertTriangle, Bug, CloudOff, ShieldAlert, CircuitBoard, PlugZap,
} from 'lucide-react';
import { NoticeIcon } from './NoticeIcon';
import type { Notice } from '../../design/components';

const loadingMessages: [string, LucideIcon][] = [
  // Whimsical
  ['Puttering', Cog],
  ['Moseying', Footprints],
  ['Fiddling', Shuffle],
  ['Sauntering', Snail],
  ['Tinkering', Hammer],
  ['Noodling', BrainCircuit],
  ['Meandering', Waypoints],
  ['Mulling', Coffee],
  ['Doodling', Pencil],
  ['Percolating', Coffee],
  ['Ambling', Footprints],
  ['Lollygagging', Armchair],
  ['Gallivanting', Map],
  ['Cogitating', Cog],

  // Grand / magical
  ['Enchanting', Wand],
  ['Assembling', Blocks],
  ['Summoning', Wand2],
  ['Conjuring', Sparkles],
  ['Orchestrating', Music2],
  ['Igniting', Zap],
  ['Crystallizing', Gem],
  ['Unraveling', Orbit],
  ['Composing', Feather],
  ['Forging', Flame],
  ['Sculpting', Palette],
  ['Launching', Rocket],
  ['Weaving', GitBranch],
  ['Kindling', Flame],
  ['Transmuting', FlaskConical],
  ['Unfurling', Wind],

  // Cozy
  ['Purring', Cat],
  ['Brewing', Coffee],
  ['Daydreaming', Sun],
  ['Snacking', Cookie],
  ['Cocooning', Flower2],
  ['Sipping', CupSoda],
  ['Humming', Music2],
  ['Sparkling', Sparkles],
  ['Vibing', Headphones],
  ['Meditating', Flower2],
  ['Blossoming', Flower],
  ['Wandering', MapPin],
];

const unknownMessages: [string, LucideIcon][] = [
  ['Lost in the woods', TreePine],
  ['Gone wandering', Compass],
  ['Vanished', Ghost],
  ['Off the map', Map],
  ['Into the fog', CloudFog],
  ['Sailed away', Anchor],
  ['Stargazing', Telescope],
  ['Out of sight', Binoculars],
  ['Over the hill', Mountain],
  ['Unplugged', Unplug],
  ['In the bushes', Shrub],
  ['Gone dark', MoonStar],
  ['Off the grid', Tent],
  ['Beachcombing', Shell],
  ['Wrong turn', Milestone],
];

const errorMessages: [string, LucideIcon][] = [
  ['Stumbled', AlertTriangle],
  ['Hit a wall', ShieldAlert],
  ['Short-circuited', CircuitBoard],
  ['Tripped up', Bug],
  ['Went sideways', AlertTriangle],
  ['Sparked out', PlugZap],
  ['Lost signal', CloudOff],
];

const pick = (list: [string, LucideIcon][]): [string, LucideIcon] =>
  list[Math.floor(Math.random() * list.length)];

const messageSets: Record<NonNullable<Notice['tone']>, [string, LucideIcon][]> = {
  loading: loadingMessages,
  unknown: unknownMessages,
  error: errorMessages,
};

export const randomMessageForTone = (tone: NonNullable<Notice['tone']> = 'loading'): [string, LucideIcon] =>
  pick(messageSets[tone]);

type Direction = 'lr' | 'rl';

const randomDirection = (): Direction =>
  Math.random() < 0.5 ? 'lr' : 'rl';

function useShimmer(length: number) {
  const windowSize = 1;
  const [state, setState] = useState(() => ({
    pos: -windowSize - 1,
    dir: randomDirection(),
  }));

  useEffect(() => {
    const id = setInterval(() => {
      setState(prev => {
        const nextPos = prev.pos + 1;
        if (nextPos > length + 1) {
          return { pos: -windowSize - 1, dir: randomDirection() };
        }
        return { ...prev, pos: nextPos };
      });
    }, 180);
    return () => clearInterval(id);
  }, [length]);

  return (i: number): number => {
    const idx = state.dir === 'lr' ? i : length - 1 - i;
    const dist = idx - state.pos;
    return (dist >= 0 && dist < windowSize) ? 1 : 0;
  };
}

const pulseKeyframes = `
@keyframes mksys-pulse { 0%, 100% { opacity: 0.25; } 40%, 60% { opacity: 1; } }
@media (prefers-reduced-motion: reduce) {
  .mksys-pulse { animation: none !important; opacity: 0.7; }
  .mksys-letter { transition: none !important; }
}
`;

/**
 * ShimmerText — animated letter-by-letter highlight with pulsing icon.
 */
export const ShimmerText: React.FC<{
  text: string;
  Icon: LucideIcon | null;
  colors: [string, string];
  iconColor?: string;
}> = ({ text, Icon, colors, iconColor }) => {
  const letters = text.split('');
  const getLevel = useShimmer(letters.length);

  return (
    <>
      <style>{pulseKeyframes}</style>
      {Icon
        ? <span className="mksys-pulse" style={{ animation: 'mksys-pulse 2s ease-in-out infinite', display: 'inline-flex' }}><Icon size={22} strokeWidth={1.5} color={iconColor} /></span>
        : <NoticeIcon size={22} />
      }
      <span style={{ fontSize: '1.125rem', whiteSpace: 'pre' }}>
        {letters.map((ch, i) => {
          const level = ch === ' ' ? 0 : getLevel(i);
          return (
            <span key={i} className="mksys-letter" style={{
              color: colors[level],
              display: 'inline-block',
            }}>{ch}</span>
          );
        })}
      </span>
    </>
  );
};

/**
 * StaticText — non-animated icon + text for static notices.
 */
export const StaticText: React.FC<{
  text: string;
  Icon: LucideIcon;
  color: string;
  iconColor?: string;
}> = ({ text, Icon, color, iconColor }) => (
  <>
    <Icon size={22} strokeWidth={1.5} color={iconColor ?? color} />
    <span style={{ fontSize: '1.125rem', color }}>{text}</span>
  </>
);
